import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useIsMutating } from "@tanstack/react-query";
import type { UploadTask } from "firebase/storage";
import { AlertTriangle, FileArchive, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EXPORT_MUTATION_KEY, useImportData, useInspectImport } from "@/hooks/use-data";
import { describeCallableError, type ImportBackupResult } from "@/lib/callables";
import {
  checkImportFile,
  describeZipUploadError,
  startBackgroundDownload,
  uploadImportZip,
  type ImportProgress,
  type PickedZip,
  type StepState,
} from "@/lib/dataTransfer";
import { projectId } from "@/lib/firebase";
import { ImportConfirmation } from "./ImportConfirmation";
import { ImportFileStatus } from "./ImportFileStatus";
import { ImportProgressList } from "./ImportProgressList";
import { Step } from "./Step";

/**
 * A importação em dois passos:
 *
 * 1. o ZIP sobe direto ao Storage, em `imports/{uid}/`, e o servidor o lê e
 *    confere inteiro, sem gravar nada (`adminInspectImport`). O resumo mostra
 *    o que vai entrar no lugar do que existe hoje;
 * 2. depois da confirmação dupla (`ImportConfirmation`), o servidor gera o
 *    backup de tudo, que o painel baixa, e só então troca o Storage e o
 *    Firestore pelos do ZIP. Sem backup, nada é trocado; o servidor confere
 *    isso de novo, e o backup não depende do navegador.
 */
export function ImportCard() {
  const inspector = useInspectImport();
  const { backup, apply } = useImportData();
  const exporting = useIsMutating({ mutationKey: EXPORT_MUTATION_KEY }) > 0;

  const [picked, setPicked] = useState<PickedZip | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // Os envios em andamento, para cancelar pelo botão e ao sair da tela. O
  // contador descarta o andamento de um envio que já foi trocado por outro.
  const uploads = useRef(new Set<UploadTask>());
  const attempt = useRef(0);

  const reading = picked?.state === "uploading" || picked?.state === "inspecting";
  const importing = backup.isPending || apply.isPending;
  const imported = progress?.apply === "done";
  const ready = picked?.state === "ready" ? picked : null;
  const summary = ready?.inspection.summary;
  const empty = summary != null && summary.firestore.documents === 0 && summary.storage.files === 0;
  const otherProject =
    ready?.inspection.otherProject && summary?.projectId ? summary.projectId : null;
  const currentProjectId = ready?.inspection.currentProjectId ?? projectId;

  // Fechar a aba cancela o envio e, no meio da troca, faz o administrador
  // perder o resultado e o link do backup (a troca segue no servidor).
  useEffect(() => {
    if (!reading && !importing) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [reading, importing]);

  // Ir para outra tela no meio do envio para o envio: o ZIP pela metade não
  // serviria para nada.
  useEffect(() => {
    const running = uploads.current;
    return () => running.forEach((task) => task.cancel());
  }, []);

  async function pickFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Permite escolher o mesmo arquivo de novo depois de um erro.
    event.target.value = "";
    if (!file) return;
    uploads.current.forEach((task) => task.cancel());
    setProgress(null);
    const id = ++attempt.current;
    const base = { name: file.name, size: file.size };

    const invalid = checkImportFile(file);
    if (invalid) {
      setPicked({ ...base, state: "failed", error: invalid });
      return;
    }

    let path: string;
    setPicked({ ...base, state: "uploading", fraction: 0 });
    try {
      const upload = uploadImportZip(file, (fraction) => {
        if (attempt.current === id) setPicked({ ...base, state: "uploading", fraction });
      });
      uploads.current.add(upload.task);
      try {
        path = await upload.done;
      } finally {
        uploads.current.delete(upload.task);
      }
    } catch (error) {
      if (attempt.current !== id) return;
      if ((error as { code?: string })?.code === "storage/canceled") {
        setPicked(null);
        toast.info("Envio cancelado.");
      } else {
        setPicked({ ...base, state: "failed", error: describeZipUploadError(error) });
      }
      return;
    }

    if (attempt.current !== id) return;
    setPicked({ ...base, state: "inspecting" });
    try {
      const inspection = await inspector.mutateAsync(path);
      if (attempt.current === id) setPicked({ ...base, state: "ready", path, inspection });
    } catch (error) {
      if (attempt.current === id) {
        setPicked({ ...base, state: "failed", error: describeCallableError(error) });
      }
    }
  }

  async function runImport(input: { phrase: string; acceptProjectMismatch: boolean }) {
    if (!ready) return;
    setConfirming(false);
    const { path } = ready;

    // 1. O backup, obrigatório: sem ele, o servidor não troca nada.
    setProgress({ backup: "running", apply: "pending" });
    let backupResult: ImportBackupResult;
    try {
      backupResult = await backup.mutateAsync({
        stage: "backup",
        path,
        confirmation: input.phrase,
        acceptProjectMismatch: input.acceptProjectMismatch,
      });
    } catch (error) {
      setProgress({
        backup: "failed",
        apply: "pending",
        error: `O backup não foi feito, e por isso nada foi importado. ${describeCallableError(error)}`,
      });
      return;
    }
    startBackgroundDownload(backupResult.backupUrl);

    // 2. A troca, com o backup que acabou de ficar pronto.
    setProgress({ backup: "done", apply: "running", backupResult });
    try {
      const result = await apply.mutateAsync({
        stage: "apply",
        path,
        confirmation: input.phrase,
        importId: backupResult.importId,
      });
      setProgress({
        backup: "done",
        apply: result.success ? "done" : "failed",
        backupResult,
        result,
        error: result.success ? undefined : result.message,
      });
      if (result.success) toast.success("Importação concluída.");
    } catch (error) {
      setProgress({
        backup: "done",
        apply: "failed",
        backupResult,
        error: describeCallableError(error),
      });
    }
  }

  const pickState: StepState =
    picked == null
      ? "pending"
      : picked.state === "ready"
        ? "done"
        : picked.state === "failed"
          ? "failed"
          : "running";
  const importState: StepState =
    progress == null
      ? "pending"
      : importing
        ? "running"
        : imported
          ? "done"
          : "failed";

  return (
    <Card className="border-destructive/60">
      <CardHeader>
        <CardTitle>Importar</CardTitle>
        <CardDescription>
          Substitui o Firestore e o Storage pelos do ZIP: o que não estiver nele é apagado.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-md border border-state-warning/40 bg-state-warning/10 p-3">
          <p className="flex items-start gap-2 text-caption">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-state-warning" />
            <span>
              Antes de trocar, o servidor gera um backup de tudo (Firestore e Storage), que o painel
              baixa e que fica guardado no Storage. Se o backup falhar, nada é importado. Para voltar
              atrás, importe o backup.
            </span>
          </p>
        </div>

        <Step number={1} title="Enviar e conferir o arquivo" state={pickState}>
          {/* O campo nativo fica escondido: o texto dele ("Nenhum arquivo
              escolhido") vem do navegador, e voltaria a aparecer depois de
              cada escolha, porque o valor é limpo para aceitar o mesmo arquivo
              de novo. O resumo abaixo diz qual foi escolhido. */}
          <input
            ref={fileInput}
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            className="sr-only"
            aria-label="Arquivo ZIP da exportação"
            onChange={(event) => void pickFile(event)}
            disabled={importing}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => fileInput.current?.click()}
              disabled={reading || importing}
            >
              <FileArchive /> {picked ? "Escolher outro arquivo" : "Escolher o ZIP"}
            </Button>
            {picked?.state === "uploading" ? (
              <Button variant="ghost" onClick={() => uploads.current.forEach((task) => task.cancel())}>
                <X /> Cancelar o envio
              </Button>
            ) : null}
          </div>

          {picked ? (
            <ImportFileStatus
              picked={picked}
              otherProject={otherProject}
              currentProjectId={currentProjectId}
              empty={empty}
            />
          ) : null}
        </Step>

        <Step number={2} title="Importar" state={importState}>
          <div>
            <Button
              variant="destructive"
              onClick={() => setConfirming(true)}
              disabled={!ready || empty || importing || imported || exporting}
            >
              <Upload /> Importar este arquivo
            </Button>
          </div>
          {exporting ? (
            <p className="text-caption text-muted-foreground">
              Espere a exportação em andamento terminar para importar.
            </p>
          ) : null}
          {imported ? (
            <p className="text-caption text-muted-foreground">
              O ZIP importado foi apagado do servidor. Para importar de novo, envie o arquivo outra
              vez.
            </p>
          ) : null}
          {progress ? <ImportProgressList progress={progress} running={importing} /> : null}
        </Step>
      </CardContent>

      {confirming && ready && summary ? (
        <ImportConfirmation
          fileName={ready.name}
          summary={summary}
          currentProjectId={currentProjectId}
          otherProject={otherProject}
          onCancel={() => setConfirming(false)}
          onConfirm={(input) => void runImport(input)}
        />
      ) : null}
    </Card>
  );
}
